/**
 * Meeting Simulator Page
 * Protected route that displays meeting categories and modules from backend
 * Integrates with /api/v2/teacher/meeting/modules/meeting endpoint
 */

import { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useProduct } from "@/hooks/useProduct";
import { getMeetingModules } from "@/store/slices/meetingModulesSlice";
import {
  selectAllCategories,
  selectCategoriesLoading,
} from "@/store/selectors/meetingModulesSelectors";
import { logout } from "@/store/slices/authSlice";
import { useRevenueCatIntegration } from "@/hooks/useRevenueCatIntegration";
import { Mic, Search, MessageSquare, Brain } from "lucide-react";
import { Input } from "@/components/ui/input";
import { CategorySection } from "@/components/CategorySection";
import { Meeting } from "@/data/meetings";
import { Header } from "@/components/layout/Header";
import { scrollToCategoryId } from "@/utils/categoryId";
import { logger } from "@/utils/logger";
import { SEOPresets } from "@/components/SEO";

const MeetingSimulator = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const config = useProduct();

  // Selectors
  const categories = useAppSelector(selectAllCategories);
  const loading = useAppSelector(selectCategoriesLoading);
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  // Check premium access - use hook function that validates isActive status
  const { hasPremiumAccess: checkPremiumAccess } = useRevenueCatIntegration();
  const hasPremiumAccess = checkPremiumAccess();

  const handleLogout = async () => {
    await dispatch(logout());
    navigate("/login");
  };

  // Fetch data on mount
  useEffect(() => {
    dispatch(getMeetingModules({}));
  }, [dispatch]);

  // Handle scroll to category anchor with robust retry logic OR scroll to top
  useEffect(() => {
    if (location.hash) {
      const categoryId = location.hash.substring(1); // Remove the '#'
      logger.log(
        `[MeetingSimulator] Detected hash: #${categoryId}, initiating scroll`
      );

      // Wait for data to load and DOM to be ready before scrolling
      const scrollTimeout = setTimeout(() => {
        scrollToCategoryId(categoryId, {
          maxRetries: 10, // Increased retries for better reliability
          retryDelay: 300, // Slightly longer delay between retries
          offset: 100, // Offset for header and spacing
        });
      }, 500); // Initial delay to ensure data is loaded

      return () => clearTimeout(scrollTimeout);
    } else {
      // No hash in URL, scroll to top
      logger.log("[MeetingSimulator] No hash detected, scrolling to top");
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [location.hash, categories]); // Re-run when categories data changes

  // Convert backend data to simplified format for display
  const convertedData = useMemo(() => {
    if (!categories || categories.length === 0) return [];

    return categories.flatMap((category) =>
      category.modules
        .filter((module) => module.level && module.level.cefr) // Filter out modules without level
        .map((module) => ({
          id: module.id,
          name: module.module_name,
          category: category.name,
          level:
            module.level.cefr === "B1"
              ? "Advanced"
              : module.level.cefr === "B2" || module.level.cefr === "C1"
                ? "Advanced"
                : ("Beginner" as "Beginner" | "Intermediate" | "Advanced"),
          questions: ["", "", ""] as [string, string, string], // Placeholder for type compatibility
          openAccess: hasPremiumAccess ? true : module.open_access,
          premiumUnlocked: hasPremiumAccess && !module.open_access, // True if premium access unlocked this module
        }))
    );
  }, [categories, hasPremiumAccess]);

  // Group by category for rendering
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) {
      return categories.map((category) => ({
        category: category.name,
        icon: category.icon,
        color: category.color,
        meetings: convertedData.filter((m) => m.category === category.name),
      }));
    }

    const query = searchQuery.toLowerCase();
    const filtered = convertedData.filter((meeting) =>
      meeting.name.toLowerCase().includes(query)
    );

    // Get unique categories that have filtered meetings
    const categoriesWithMeetings = categories.filter((category) =>
      filtered.some((m) => m.category === category.name)
    );

    return categoriesWithMeetings.map((category) => ({
      category: category.name,
      icon: category.icon,
      color: category.color,
      meetings: filtered.filter((m) => m.category === category.name),
    }));
  }, [searchQuery, convertedData, categories]);

  return (
    <div className="min-h-screen bg-background">
      {SEOPresets.meetingSimulator}
      <Header showBackButton={true} />

      {/* Hero Section - starts right after header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary via-primary/90 to-accent py-24 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzBoLTJWMThoMnYxMnptLTEyIDBIMjJWMThoMnYxMnptMjQgMGgtMlYxOGgydjEyem0tMTIgMGgtMlYxOGgydjEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-10" />
        <div className="relative max-w-7xl mx-auto animate-fade-in">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center animate-scale-in">
                <Mic className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              {config.i18n.meetingSimulator.heroTitle}
            </h1>
            <p className="text-lg sm:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              {convertedData.length > 0
                ? config.i18n.meetingSimulator.heroSubtitleWithCount(
                    convertedData.length
                  )
                : config.i18n.meetingSimulator.heroSubtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8 max-w-2xl mx-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder={config.i18n.meetingSimulator.searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 text-base"
            />
          </div>
        </div>

        {loading && convertedData.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg">
              {config.i18n.meetingSimulator.loadingMeetings}
            </p>
          </div>
        ) : filteredCategories.length > 0 ? (
          filteredCategories.map(({ category, icon, color, meetings }) => (
            <CategorySection
              key={category}
              category={category}
              icon={icon}
              color={color}
              meetings={meetings}
            />
          ))
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg">
              {config.i18n.meetingSimulator.noMeetingsFound(searchQuery)}
            </p>
          </div>
        )}
      </div>

      {/* Professional Footer */}
      <footer className="relative bg-gradient-to-br from-primary via-accent to-primary mt-20">
        {/* Decorative grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:64px_64px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Main footer content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* About section */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-lg mb-4">
                {config.i18n.footer.companyName}
              </h3>
              <p className="text-white/80 text-sm leading-relaxed">
                {config.i18n.footer.companyDescription}
              </p>
            </div>

            {/* Quick Links */}

            {/* Resources */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-lg mb-4">
                {config.i18n.footer.resources}
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/faq"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    {config.i18n.footer.faq}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy-policy"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    {config.i18n.footer.privacyPolicy}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms-of-service"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    {config.i18n.footer.termsOfService}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-lg mb-4">
                {config.i18n.footer.contact}
              </h3>
              <p className="text-white/80 text-sm leading-relaxed">
                {config.i18n.footer.contactDescription}
              </p>
              <div className="flex gap-4">
                <a
                  href="#"
                  className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-200"
                  aria-label="LinkedIn"
                >
                  <MessageSquare className="w-5 h-5 text-white" />
                </a>
                <a
                  href="#"
                  className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-200"
                  aria-label="Email"
                >
                  <Brain className="w-5 h-5 text-white" />
                </a>
              </div>
            </div>
          </div>

          {/* Funding section */}
          <div className="border-t border-white/10 pt-8 pb-8">
            <div className="flex justify-center items-center mb-8">
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-3 shadow-2xl">
                <img
                  src={
                    new URL("../assets/footer-funding.jpeg", import.meta.url)
                      .href
                  }
                  alt={config.i18n.footer.fundingAlt}
                  className="max-w-full h-auto"
                  style={{
                    maxHeight: "140px",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-white/60 text-sm">
                {config.i18n.footer.copyright(new Date().getFullYear())}
              </p>
              <div className="flex items-center gap-2 text-white/60 text-sm">
                {config.i18n.footer.madeWith}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MeetingSimulator;
